import React, { useImperativeHandle, useRef, forwardRef } from "react";
import { getErrorModuleStatus, isEqual, refIsRequiredError } from "@util/utils";
import { Grid } from "@mui/material";
import { FieldRow } from "@icoach/documents/components/Layout";
import { Button, TextField } from "@curves/ui-base";
import { LeaveTextField } from "@icoach/documents/components";
import { useMembershipQuitFormContext } from "@icoach/documents/membership/quit/MembershipQuitFormContext";

const MembershipQuitTrialCalculation101 = forwardRef((_, ref) => {
	const {
		formData: sourceData = {},
		validationErrors,
		updateField,
		dialogRef,
		isReadOnly,
		manualTriggerRangeFee,
	} = useMembershipQuitFormContext();

	const {
		lastMonthlyFee,
		firstMonthDay,
		suspendMonth,
		suspendDay,
		giftMonth,
		giftDay,
		unusedExtensionMonth,
		unusedExtensionDay,
		clubBreakMonth,
		clubBreakDay,
		contractMonthTerm,
		usedMonth,
		usedDay,
		unusedMonth,
		unusedDay
	} = sourceData || {};

	const fieldRef = useRef({});
	const dateRef = useRef({});

	useImperativeHandle(ref, () => ({
			getResult: () => {
				let fieldObj = fieldRef.current;
				let dateObj = dateRef.current;
				let result = {};

				if (fieldObj) {
					for (let key in fieldObj) {
						const target = fieldObj[key];

						if (target && target.getResult) {
							let val = target.getResult();
							if (isEqual(key, "contractMonthTerm")) {
								result.contractMonthTerm = val?.month ?? 0;
							} else {
								result[key] = val;
							}
						}
					}
				}

				if (dateObj) {
					for (let key in dateObj) {
						const target = dateObj[key];
						if (target && target.getResult) {
							const { day, month } = target.getResult();
							if (day) {
								result[`${key}Day`] = day;
							}

							if (month) {
								result[`${key}Month`] = month;
							}
						}
					}
				}

				return result;
			},
			isError: () => {
				const allObj = {
					...fieldRef.current,
					...dateRef.current
				};
				return refIsRequiredError(Object.values(allObj));
			}
		}),
		// eslint-disable-line
		[]);

	return (
		<Grid container spacing={3}>
			<FieldRow items={[4, 8]} label="當月已收月費" required>
				<TextField
					ref={el => fieldRef.current.lastMonthlyFee = el}
					key={lastMonthlyFee}
					defaultValue={lastMonthlyFee}
					onChangeNative={(e) => updateField("lastMonthlyFee", e.target.value)}
					{...getErrorModuleStatus(validationErrors, "lastMonthlyFee")}
					readOnly={isReadOnly}
					maskType={"MONEY"}
					fullWidth
				/>
			</FieldRow>
			<FieldRow items={[4, 8]} label="破月日" required>
				<TextField
					ref={el => (fieldRef.current.firstMonthDay = el)}
					key={firstMonthDay}
					defaultValue={firstMonthDay}
					onChangeNative={(e) => updateField("firstMonthDay", e.target.value)}
					{...getErrorModuleStatus(validationErrors, "firstMonthDay")}
					readOnly={isReadOnly}
					fullWidth
				/>
			</FieldRow>
			<FieldRow items={[4, 8]} label="會員權暫停" required>
				<LeaveTextField
					ref={el => (dateRef.current.suspend = el)}
					monthProps={{
						key: suspendMonth,
						defaultValue: suspendMonth,
						onChangeNative: e => updateField("suspendMonth", e.target.value),
						inputProps: { min: 0 },
						maskType: "MONEY"
					}}
					dayProps={{
						key: suspendDay,
						defaultValue: suspendDay,
						onChangeNative: e => updateField("suspendDay", e.target.value),
						inputProps: { min: 0 },
						maskType: "MONEY"
					}}
					readOnly={isReadOnly}
					isShowDay
					fullWidth
				/>
			</FieldRow>
			<FieldRow items={[4, 8]}>
				<Button variant="outlined" color="secondary" className="btn-minWidth" fullWidth
						onClick={() => dialogRef?.current?.openSuspendReasonsDialog?.({
							memberID: sourceData?.memberID,
							membershipID: sourceData?.membershipID
						})}>
					查看暫停紀錄
				</Button>
			</FieldRow>
			<FieldRow items={[4, 8]} label="贈送" required>
				<LeaveTextField
					ref={el => (dateRef.current.gift = el)}
					monthProps={{
						key: giftMonth,
						defaultValue: giftMonth,
						onChangeNative: e => updateField("giftMonth", e.target.value),
						inputProps: { min: 0 },
						maskType: "MONEY"
					}}
					dayProps={{
						key: giftDay,
						defaultValue: giftDay,
						onChangeNative: e => updateField("giftDay", e.target.value),
						inputProps: { min: 0 },
						maskType: "MONEY"
					}}
					readOnly={isReadOnly}
					isShowDay
					fullWidth
				/>
			</FieldRow>
			<FieldRow items={[4, 8]}>
				<Button variant="outlined" color="secondary" className="btn-minWidth" fullWidth
						onClick={() => dialogRef?.current?.openGiftReasonsDialog?.({
							memberID: sourceData?.memberID,
							membershipID: sourceData?.membershipID
						})}>
					查看贈送紀錄
				</Button>
			</FieldRow>
			<FieldRow items={[4, 8]} label="延展" required>
				<LeaveTextField
					ref={el => (dateRef.current.unusedExtension = el)}
					monthProps={{
						key: unusedExtensionMonth,
						defaultValue: unusedExtensionMonth,
						onChangeNative: e => updateField("unusedExtensionMonth", e.target.value),
						inputProps: { min: 0 },
						maskType: "MONEY"
					}}
					dayProps={{
						key: unusedExtensionDay,
						defaultValue: unusedExtensionDay,
						onChangeNative: e => updateField("unusedExtensionDay", e.target.value),
						inputProps: { min: 0 },
						maskType: "MONEY"
					}}
					readOnly={isReadOnly}
					isShowDay
					fullWidth
				/>
			</FieldRow>
			<FieldRow items={[4, 8]}>
				<Button variant="outlined" color="secondary" className="btn-minWidth" fullWidth
						onClick={() => dialogRef?.current?.openExtensionReasonsDialog?.({
							memberID: sourceData?.memberID,
							membershipID: sourceData?.membershipID
						})}>
					查看延展紀錄
				</Button>
			</FieldRow>
			<FieldRow items={[4, 8]} label="店休" required>
				<LeaveTextField
					monthProps={{
						key: clubBreakMonth,
						defaultValue: clubBreakMonth,
						onChangeNative: e => updateField("clubBreakMonth", e.target.value),
						inputProps: { min: 0 },
						maskType: "MONEY"
					}}
					dayProps={{
						key: clubBreakDay,
						defaultValue: clubBreakDay,
						onChangeNative: e => updateField("clubBreakDay", e.target.value),
						inputProps: { min: 0 },
						maskType: "MONEY"
					}}
					disabled
					isShowDay
					fullWidth
				/>
			</FieldRow>
			<FieldRow items={[4, 8]} label={"合約總共"} required>
				<LeaveTextField
					ref={el => (fieldRef.current.contractMonthTerm = el)}
					monthProps={{
						key: contractMonthTerm,
						defaultValue: contractMonthTerm,
						onChangeNative: e => updateField("contractMonthTerm", e.target.value),
						inputProps: { min: 0 },
						maskType: "MONEY"
					}}
					readOnly={isReadOnly}
					fullWidth
				/>
			</FieldRow>
			<FieldRow items={[4, 8]} label="已使用">
				<LeaveTextField
					ref={el => (dateRef.current.used = el)}
					monthProps={{
						key: usedMonth,
						defaultValue: usedMonth,
						onChangeNative: e => updateField("usedMonth", e.target.value),
						inputProps: { min: 0 },
						maskType: "MONEY"
					}}
					dayProps={{
						key: usedDay,
						defaultValue: usedDay,
						onChangeNative: e => updateField("usedDay", e.target.value),
						inputProps: { min: 0 },
						maskType: "MONEY"
					}}
					isShowDay
					fullWidth
					disabled
				/>
			</FieldRow>
			<FieldRow items={[4, 8]} label="未履約">
				<LeaveTextField
					ref={el => (dateRef.current.unused = el)}
					monthProps={{
						key: unusedMonth,
						defaultValue: unusedMonth,
						onChangeNative: e => updateField("unusedMonth", e.target.value),
						inputProps: { min: 0 },
						maskType: "MONEY"
					}}
					dayProps={{
						key: unusedDay,
						defaultValue: unusedDay,
						onChangeNative: e => updateField("unusedDay", e.target.value),
						inputProps: { min: 0 },
						maskType: "MONEY"
					}}
					isShowDay
					fullWidth
					disabled
				/>
			</FieldRow>
			{!isReadOnly && (
				<FieldRow items={[4, 8]}>
					<Button variant="contained" color="secondary" className="btn-minWidth"
							onClick={manualTriggerRangeFee} readOnly={isReadOnly} fullWidth>
						計算
					</Button>
				</FieldRow>
			)}
		</Grid>
	);
});

export default MembershipQuitTrialCalculation101;
